import * as Schema from "effect/Schema";
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
} from "react";

import { getLocalStorageItem, setLocalStorageItem } from "../../hooks/useLocalStorage";
import { ScrollArea } from "../ui/scroll-area";
import { useSidebar } from "../ui/sidebar";
import {
  DEFAULT_SIDEBAR_SPLIT_RATIO,
  MAX_SIDEBAR_SPLIT_RATIO,
  MIN_SIDEBAR_SPLIT_RATIO,
  clampSidebarSplitRatio,
  sidebarSplitRatioFromKey,
  sidebarSplitRatioFromPointer,
} from "./SidebarVerticalSplit.logic";

const SIDEBAR_SPLIT_RATIO_STORAGE_KEY = "praxa:sidebar:vertical-split-ratio:v1";

function readInitialSplitRatio(): number {
  if (typeof window === "undefined") {
    return DEFAULT_SIDEBAR_SPLIT_RATIO;
  }
  try {
    return clampSidebarSplitRatio(
      getLocalStorageItem(SIDEBAR_SPLIT_RATIO_STORAGE_KEY, Schema.Finite) ??
        DEFAULT_SIDEBAR_SPLIT_RATIO,
    );
  } catch (error) {
    console.error("Could not restore the sidebar split position.", error);
    return DEFAULT_SIDEBAR_SPLIT_RATIO;
  }
}

function persistSplitRatio(ratio: number) {
  try {
    setLocalStorageItem(
      SIDEBAR_SPLIT_RATIO_STORAGE_KEY,
      clampSidebarSplitRatio(ratio),
      Schema.Finite,
    );
  } catch (error) {
    console.error("Could not save the sidebar split position.", error);
  }
}

export function SidebarVerticalSplit(props: {
  readonly bottom: ReactNode;
  readonly top: ReactNode;
}) {
  const { isMobile } = useSidebar();
  const [ratio, setRatio] = useState(readInitialSplitRatio);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const topId = useId();
  const bottomId = useId();
  const dragStateRef = useRef<{
    readonly containerHeight: number;
    readonly pointerId: number;
    readonly startRatio: number;
    readonly startY: number;
    readonly target: HTMLButtonElement;
    pendingRatio: number;
    rafId: number | null;
  } | null>(null);

  const releasePointer = useCallback((pointerId: number) => {
    const dragState = dragStateRef.current;
    if (!dragState) return;
    if (dragState.rafId !== null) {
      window.cancelAnimationFrame(dragState.rafId);
    }
    try {
      if (dragState.target.hasPointerCapture(pointerId)) {
        dragState.target.releasePointerCapture(pointerId);
      }
    } catch {
      // The browser may have already released capture after a cancelled drag.
    }
    document.body.style.removeProperty("cursor");
    document.body.style.removeProperty("user-select");
    dragStateRef.current = null;
  }, []);

  useEffect(
    () => () => {
      const dragState = dragStateRef.current;
      if (dragState?.rafId != null) {
        window.cancelAnimationFrame(dragState.rafId);
      }
      document.body.style.removeProperty("cursor");
      document.body.style.removeProperty("user-select");
    },
    [],
  );

  const onPointerDown = useCallback(
    (event: PointerEvent<HTMLButtonElement>) => {
      if (event.button !== 0) return;
      const containerHeight = containerRef.current?.getBoundingClientRect().height ?? 0;
      if (containerHeight <= 0) return;
      event.preventDefault();
      event.stopPropagation();
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        return;
      }
      document.body.style.cursor = "row-resize";
      document.body.style.userSelect = "none";
      dragStateRef.current = {
        containerHeight,
        pointerId: event.pointerId,
        startRatio: ratio,
        startY: event.clientY,
        target: event.currentTarget,
        pendingRatio: ratio,
        rafId: null,
      };
    },
    [ratio],
  );

  const onPointerMove = useCallback((event: PointerEvent<HTMLButtonElement>) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) return;
    event.preventDefault();
    dragState.pendingRatio = sidebarSplitRatioFromPointer({
      containerHeight: dragState.containerHeight,
      currentY: event.clientY,
      startRatio: dragState.startRatio,
      startY: dragState.startY,
    });
    if (dragState.rafId !== null) return;
    dragState.rafId = window.requestAnimationFrame(() => {
      const activeDragState = dragStateRef.current;
      if (!activeDragState) return;
      activeDragState.rafId = null;
      setRatio(activeDragState.pendingRatio);
    });
  }, []);

  const onPointerUp = useCallback(
    (event: PointerEvent<HTMLButtonElement>) => {
      const dragState = dragStateRef.current;
      if (!dragState || dragState.pointerId !== event.pointerId) return;
      const nextRatio = clampSidebarSplitRatio(dragState.pendingRatio);
      releasePointer(event.pointerId);
      setRatio(nextRatio);
      persistSplitRatio(nextRatio);
    },
    [releasePointer],
  );

  const onPointerCancel = useCallback(
    (event: PointerEvent<HTMLButtonElement>) => {
      const dragState = dragStateRef.current;
      if (!dragState || dragState.pointerId !== event.pointerId) return;
      const startRatio = dragState.startRatio;
      releasePointer(event.pointerId);
      setRatio(startRatio);
    },
    [releasePointer],
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>) => {
      if (
        event.key !== "ArrowDown" &&
        event.key !== "ArrowUp" &&
        event.key !== "Home" &&
        event.key !== "End"
      ) {
        return;
      }
      event.preventDefault();
      const nextRatio = sidebarSplitRatioFromKey(ratio, event.key);
      setRatio(nextRatio);
      persistSplitRatio(nextRatio);
    },
    [ratio],
  );

  if (isMobile) {
    return (
      <div
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
        data-sidebar-vertical-split="stacked"
      >
        <div>{props.top}</div>
        <div>{props.bottom}</div>
      </div>
    );
  }

  const ratioPercent = Math.round(ratio * 100);

  return (
    <div
      ref={containerRef}
      className="flex min-h-0 flex-1 flex-col overflow-hidden"
      data-sidebar-vertical-split=""
    >
      <ScrollArea
        className="min-h-0 shrink-0 rounded-none"
        hideScrollbars
        scrollFade
        style={{ height: `${ratioPercent}%` }}
      >
        <div id={topId}>{props.top}</div>
      </ScrollArea>
      <button
        aria-controls={`${topId} ${bottomId}`}
        aria-label="Resize sidebar sections"
        aria-orientation="horizontal"
        aria-valuemax={Math.round(MAX_SIDEBAR_SPLIT_RATIO * 100)}
        aria-valuemin={Math.round(MIN_SIDEBAR_SPLIT_RATIO * 100)}
        aria-valuenow={ratioPercent}
        aria-valuetext={`Navigation uses ${ratioPercent} percent of the sidebar height`}
        className="group relative z-10 h-2 shrink-0 cursor-row-resize border-0 bg-transparent p-0 outline-none focus-visible:ring-1 focus-visible:ring-ring"
        data-sidebar-splitter=""
        onDoubleClick={() => {
          setRatio(DEFAULT_SIDEBAR_SPLIT_RATIO);
          persistSplitRatio(DEFAULT_SIDEBAR_SPLIT_RATIO);
        }}
        onKeyDown={onKeyDown}
        onPointerCancel={onPointerCancel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        role="separator"
        tabIndex={0}
        type="button"
      >
        <span
          aria-hidden
          className="absolute inset-x-2 top-1/2 h-px -translate-y-1/2 bg-sidebar-border transition-colors group-hover:bg-sidebar-muted-foreground/45 group-focus-visible:bg-sidebar-muted-foreground/55"
        />
      </button>
      <ScrollArea className="min-h-0 flex-1 rounded-none" hideScrollbars scrollFade>
        <div id={bottomId}>{props.bottom}</div>
      </ScrollArea>
    </div>
  );
}
